'use client';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';
import { type FC, useMemo } from 'react';

type Props = {
  items: any;
  color?: string;
  width?: string;
  height?: string;
  isBlury?: boolean;
};

// Register the required components
echarts.use([TooltipComponent, GridComponent, BarChart, SVGRenderer]);

export const HelioSecondCircleBarGraph: FC<Props> = (props: Props) => {
  const { items, isBlury = false, width = '484px', height = '540px' } = props;

  const blurFilter = useMemo(
    () => (isBlury ? 'blur(4px)' : 'blur(0)'),
    [isBlury]
  );

  const theme = useTheme();

  const data = useMemo(
    () => [
      {
        name: items.second_circle_se.data.translate.name,
        value: isBlury
          ? Math.floor(Math.random() * 101)
          : items.second_circle_se.score,
      },
      {
        name: items.second_circle_otc.data.translate.name,
        value: isBlury
          ? Math.floor(Math.random() * 101)
          : items.second_circle_otc.score,
      },
      {
        name: items.second_circle_st.data.translate.name,
        value: isBlury
          ? Math.floor(Math.random() * 101)
          : items.second_circle_st.score,
      },
      {
        name: items.second_circle_c.data.translate.name,
        value: isBlury
          ? Math.floor(Math.random() * 101)
          : items.second_circle_c.score,
      },
    ],
    [items, isBlury]
  );

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        valueFormatter: (value: string) => `${value}%`,
      },
      grid: {
        left: '72px',
        right: '72px',
        top: '16px',
        bottom: 0,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map((item) => item.name),
        axisLabel: {
          show: true,
          interval: 0,
          rotate: 45,
          fontWeight: 'bolder',
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 10,
        axisLabel: {
          formatter: (value: string, index: number) => {
            return index === 0 ||
              index === 3 ||
              index === 5 ||
              index === 7 ||
              index === 10
              ? `${value}%`
              : null;
          },
        },
      },
      series: [
        {
          type: 'bar',
          data: data.map((item) => item.value),
          label: {
            show: true,
            color: theme.palette.primary.main,
            position: 'top',
            fontWeight: 'bold',
            formatter: '{c}%',
          },
          itemStyle: {
            color: theme.palette.primary.main,
          },
        },
      ],
    }),
    [data]
  );

  return (
    <Box
      component="div"
      id="helio-dynamic-trend"
      position="relative"
      width={width}
      height={height}
      sx={{ filter: blurFilter }}
    >
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        notMerge={true}
        lazyUpdate={true}
        theme={'theme_name'}
        style={{ height: '100%', width: '100%', position: 'relative' }}
        opts={{}}
      />
    </Box>
  );
};
